"use client";
import React, { useRef } from "react";
import ScrollSpy from "react-ui-scrollspy";
import Allergens from "../allergens/allergens";
import { MenuProps } from "./menu";
import { cn, priceFormatter } from "@/lib/utils";
import { typographyClass } from "../typography";
import { BlurFade } from "../ui/blur-fade";
import { RichText } from "@payloadcms/richtext-lexical/react";
import { useTranslations } from "next-intl";
import { convertTextToTextWithBr } from "@/lib/ui-utils";

export default function MenuDesktop({ menu }: MenuProps) {
  const t = useTranslations("menu");

  const dishGroups = menu?.dishGroup?.map((dishGroup) =>
    t(dishGroup.type!).toLowerCase().split(" ").join("-")
  );

  const indicatorRef = useRef<HTMLDivElement | null>(null);
  return (
    <div className=" gap-10 hidden md:flex">
      <div className="lg:mr-10 w-full flex flex-col gap-14">
        <ScrollSpy
          scrollThrottle={50}
          offsetBottom={48}
          offsetTop={48}
          onUpdateCallback={(id: string) => {
            if (!indicatorRef.current) return;

            const menuIndex = dishGroups?.indexOf(id) ?? -1;
            indicatorRef.current.style.top = `${32 + menuIndex * 32}px`;
            indicatorRef.current.style.display =
              menuIndex == -1 ? "hidden" : "block";
          }}
        >
          {menu?.dishGroup?.map((dishGroup, index) => (
            <section
              key={index}
              id={t(dishGroup.type!).toLowerCase().split(" ").join("-")}
              data-scrollspy
            >
              <BlurFade
                inView
                className={cn(
                  "flex flex-col mx-auto styled-receipt border rounded-xl border-muted/30 scroll-mt-[76px] lg:scroll-mt-[6px]",
                  {
                    "mt-20": index != 0,
                    "xl:w-1/2": dishGroup.category?.length == 1,
                  }
                )}
              >
                <div className="border-b py-4 border-muted/10">
                  <h2
                    className={cn(
                      "uppercase text-primary !font-medium mx-8 text-center",
                      typographyClass.title
                    )}
                  >
                    {t(dishGroup.type)}
                  </h2>
                </div>

                <div
                  className={cn("grid grid-cols-1 xl:grid-cols-2", {
                    "!grid-cols-1": dishGroup.category?.length == 1,
                  })}
                >
                  {dishGroup.category?.map((category, categoryIndex) => (
                    <div
                      className={cn("flex flex-col w-full", {
                        "xl:border-l border-muted/10": categoryIndex % 2 != 0,
                      })}
                      key={`dish-${categoryIndex}`}
                    >
                      {category.name && (
                        <div
                          className={cn("border-b py-4 border-muted/10", {
                            "xl:!border-t-0":
                              categoryIndex >= 0 && categoryIndex <= 1,
                            "border-t": categoryIndex > 0,
                          })}
                        >
                          <h3
                            className={cn(
                              typographyClass.subtitle,
                              "uppercase font-medium text-center"
                            )}
                          >
                            {t(category.name)}
                          </h3>
                        </div>
                      )}

                      <div className="flex flex-col py-4">
                        {category.dishes?.map((dish, dishIndex) => (
                          <div
                            className={cn("flex flex-row py-3.5 gap-5", {
                              "pt-2": dishIndex == 0,
                            })}
                            key={`dish-${categoryIndex}-${dishIndex}`}
                          >
                            <div className="flex flex-col w-full px-4 md:px-8">
                              <div>
                                <p
                                  className={cn(
                                    typographyClass.largeBody,
                                    "uppercase font-semibold inline text-primary"
                                  )}
                                >
                                  {dish.name}
                                </p>

                                {(dish.priceBrackets == null ||
                                  dish.priceBrackets!.length < 1) && (
                                  <p
                                    className={cn(
                                      typographyClass.largeBody,
                                      "inline ml-4"
                                    )}
                                  >
                                    {priceFormatter.format(dish.price!)}
                                  </p>
                                )}
                              </div>

                              {dish.priceBrackets != null &&
                                dish.priceBrackets.map(
                                  (priceBracket, priceBracketIndex) => (
                                    <div
                                      className="flex items-end"
                                      key={`${index}-${priceBracketIndex}`}
                                    >
                                      <span className="text-muted">
                                        {priceBracket.description}
                                      </span>
                                      <p
                                        className={cn(
                                          typographyClass.largeBody,
                                          "ml-4"
                                        )}
                                      >
                                        {priceFormatter.format(
                                          priceBracket.price!
                                        )}
                                      </p>
                                    </div>
                                  )
                                )}
                              <span className="text-muted">
                                {dish.description}
                              </span>
                              {dish.allergens && dish.allergens?.length > 0 && (
                                <Allergens
                                  isMobile={false}
                                  className="my-1 w-fit"
                                  allergens={dish.allergens?.toSorted()}
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {dishGroup.extra &&
                  dishGroup.extra.root.children.filter(
                    (el) => (el.children as object[]).length > 0
                  ).length > 0 && (
                    <div className="border-t border-muted/10 py-4">
                      <div className="mx-8">
                        <RichText
                          className={cn(
                            typographyClass.largeBody,
                            "text-center"
                          )}
                          data={dishGroup!.extra!}
                        />
                      </div>
                    </div>
                  )}
              </BlurFade>
            </section>
          ))}
        </ScrollSpy>
        <BlurFade
          className="bg-muted/10 p-4 rounded-xl"
          delay={0.1 * menu!.dishGroup!.length}
        >
          <p>{convertTextToTextWithBr(t("desclaimer"))}</p>
        </BlurFade>
      </div>
      <div
        className={cn(
          "sticky top-20 h-full p-4 border-l w-fit pl-8",
          "[&_.active-scroll-spy]:text-black"
        )}
      >
        <div
          ref={indicatorRef}
          className={cn(
            "h-[20px] w-[4px] absolute -left-[2px] bg-primary rounded-full transition-all duration-200 ease-in-out hidden"
          )}
        />

        <ul className="flex flex-col gap-2 mt-4">
          {menu?.dishGroup?.map((dishGroup, index) => {
            return (
              <li key={`nav-${index}`}>
                <a
                  href={`#${t(dishGroup.type!).toLowerCase().split(" ").join("-")}`}
                  data-to-scrollspy-id={`${t(dishGroup.type!).toLowerCase().split(" ").join("-")}`}
                  className={cn(
                    "block text-muted/50 transition-all duration-200 text-nowrap"
                    // { "!text-black": menuIndex == index }
                  )}
                >
                  {t(dishGroup.type!)}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
